/**
 * Auditor Signature Component
 *
 * Simple signature showing auditor photo, name and approval date.
 * Supports sepia style when reading mode is active.
 */
import React, { useState } from 'react';
import { FiUser } from 'react-icons/fi';
import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { useReadingMode } from '../../contexts/ReadingModeContext';

interface AuditorSignatureProps {
  auditorId?: string;
  auditorName?: string;
  auditorEmail?: string;
  auditorPhotoUrl?: string;
  reviewedAt?: string;
  status?: 'approved' | 'approved_with_edit' | 'rejected';
  size?: 'sm' | 'md' | 'lg';
}

const AuditorSignature: React.FC<AuditorSignatureProps> = ({
  auditorId,
  auditorName,
  auditorEmail,
  auditorPhotoUrl,
  reviewedAt,
  status,
  size = 'sm',
}) => {
  const [imageError, setImageError] = useState(false);
  const { isReadingMode } = useReadingMode();

  if (!auditorId && !auditorName) {
    return null;
  }

  const sizeConfig = {
    sm: { photo: 32, fontSize: '0.8125rem', dateFontSize: '0.7rem', padding: '0.5rem 0.75rem', gap: '0.5rem' },
    md: { photo: 40, fontSize: '0.875rem', dateFontSize: '0.75rem', padding: '0.625rem 1rem', gap: '0.625rem' },
    lg: { photo: 48, fontSize: '1rem', dateFontSize: '0.8125rem', padding: '0.75rem 1.25rem', gap: '0.75rem' },
  };

  const config = sizeConfig[size];

  // Border color based on status
  const isRejected = status === 'rejected';
  const approvedBorderColor = '#3F8D72';
  const rejectedBorderColor = '#DC2626';
  const statusBorderColor = isRejected ? rejectedBorderColor : approvedBorderColor;

  // Colors based on reading mode
  const colors = isReadingMode
    ? {
        background: '#F5F0E6',
        border: '#8B775C',
        nameColor: '#5C4A3D',
        dateColor: '#8B775C',
        photoBg: '#E8E0D5',
        photoIcon: '#8B775C',
      }
    : {
        background: '#FFFFFF',
        border: statusBorderColor,
        nameColor: '#1F2937',
        dateColor: '#6B7280',
        photoBg: '#E5E7EB',
        photoIcon: '#6B7280',
      };

  const formatReviewDate = (dateStr: string): string => {
    try {
      const date = parseISO(dateStr);
      if (!isValid(date)) return '';
      return format(date, "d 'de' MMMM, yyyy", { locale: es });
    } catch {
      return '';
    }
  };

  const displayName = auditorName || auditorEmail?.split('@')[0] || 'Auditor';
  const formattedDate = reviewedAt ? formatReviewDate(reviewedAt) : '';

  return (
    <div
      className="auditor-signature"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: config.gap,
        padding: config.padding,
        borderRadius: '8px',
        background: colors.background,
        border: `2px solid ${colors.border}`,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Photo */}
      <div
        style={{
          width: config.photo,
          height: config.photo,
          borderRadius: '50%',
          overflow: 'hidden',
          background: colors.photoBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: `2px solid ${colors.border}`,
          transition: 'all 0.3s ease',
        }}
      >
        {auditorPhotoUrl && !imageError ? (
          <img
            src={auditorPhotoUrl}
            alt={displayName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={() => setImageError(true)}
          />
        ) : (
          <FiUser size={config.photo * 0.5} color={colors.photoIcon} />
        )}
      </div>

      {/* Name & Date */}
      <div style={{ lineHeight: 1.3 }}>
        <div
          style={{
            fontSize: config.fontSize,
            fontWeight: 600,
            color: colors.nameColor,
            transition: 'color 0.3s ease',
          }}
        >
          {displayName}
        </div>
        {formattedDate && (
          <div
            style={{
              fontSize: config.dateFontSize,
              color: colors.dateColor,
              marginTop: '2px',
              transition: 'color 0.3s ease',
            }}
          >
            {formattedDate}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditorSignature;
